/**
 * Owns the version list and the view/restore lifecycle for the clip currently open in the
 * editor.
 *
 * A module-level singleton rather than hook state: several components render versioning UI
 * (the panel, the view-mode banner, the discard-edits control) and they must agree on one
 * list, one in-flight load and one Pusher subscription. `activateVersioning` is idempotent
 * per clip, so every one of them can call it on mount and only the first does any work.
 */

import {
  assetsVersionsCreate,
  assetsVersionsList,
  assetsVersionsRestoreCreate,
  assetsVersionsRetrieve
} from '@goldcast/api/content';
import { updateClip } from '@goldcast/player-config/src/retrievers/updateClip';
import { getClipVersion } from '@goldcast/player-config/src/utils/clipUtils';
import type { Clip } from '@goldcast/player-config/src/types/clip-types';
import { applyChaptersLocally, getCurrentChapters, persistChapters } from './chapters';
import { clearEditLog, reconcileEditLogAfterVersion } from './editLog';
import { buildRestoredMetadata } from './restoreMetadata';
import type { ClipVersionCreateWithChapters, ClipVersionDetailWithChapters, ClipVersionListWithAuthor } from './types';
import { stripCaptions, toDisplayVersions, VERSION_PAGE_SIZE } from './utils';
import appLogger from '@/services/logger';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { clipVersionStore } from '@/stores/clipVersionStore';
import { editorRestrictionsStore, EDITOR_RESTRICTION_REASONS } from '@/stores/editorRestrictionsStore';
import { currentClip, getIsClipUsingDefaultLanguage, updateClipMetadataWithId } from '@/stores/clip';
import { initHistory } from '@/stores/clipHistory';
import { addPendingUpdate } from '@/stores/pendingEditsStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { updateCurrentClipUpdatedAt } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/updatedAtUtils';
import { ClipVersion } from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerVersioningPanel/types';

/** Body of the `CLIP_VERSION_CREATED` Pusher event — note the clip is keyed as `asset_id`. */
interface ClipVersionCreatedPayload {
  asset_id: string;
  created_at?: string;
}

const VERSION_VIEW_BLOCKED_CAPABILITIES = [
  'clip-publish',
  'clip-download',
  'clip-create',
  'clip-undo-redo',
  'audio-mixer',
  'clip-save-template',
  // Switching language rewrites captions and saves the clip, so it would write the previewed
  // version back as new work.
  'clip-language-switch',
  'timeline-trim',
  'timeline-cut',
  'timeline-segment-dnd',
  'timeline-scene-edit',
  'timeline-scene-add',
  'timeline-transition-edit',
  'timeline-hook-add',
  'timeline-intro-outro-edit',
  'timeline-chapters-edit',
  'timeline-track-delete',
  'element-dnd',
  'element-popover',
  'element-zoom-effect',
  'element-customizer',
  'customizer-templates',
  'customizer-layout',
  'customizer-captions',
  'customizer-background',
  'customizer-images',
  'customizer-videos',
  'customizer-overlays',
  'customizer-audio',
  'transcript-edit',
  'transcript-download'
] as const;

/** The clip the editor is open on. Null when versioning is off or nothing is open. */
let activeClipId: string | null = null;

/** Incremented per load so a superseded request cannot write its result. */
let loadSequence = 0;

/** Whether a load has put the list into its skeleton state and nothing has cleared it yet. */
let isSkeletonShowing = false;

/** Set when the active clip's load failed, so the next activation retries instead of skipping. */
let hasFailedLoad = false;

let isPusherBound = false;

function isVersioningEnabled(): boolean {
  return !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Clip_Versioning];
}

// ─── Loading ──────────────────────────────────────────────────────────────────

async function fetchVersions(clipId: string): Promise<ClipVersion[]> {
  const res = await assetsVersionsList({ asset_id: clipId, queryParams: { limit: VERSION_PAGE_SIZE } });
  // The response nests the full author under created_by; the generated schema still types it
  // as the bare UUID. Drop the cast once ClipVersionList is regenerated.
  return toDisplayVersions(res.results as unknown as ClipVersionListWithAuthor[]);
}

/**
 * Loads the list into the store. `showSkeleton` is false for the background refreshes that
 * follow a save or a restore, so the list doesn't flash back to skeletons.
 */
async function loadVersionsIntoStore(clipId: string, { showSkeleton }: { showSkeleton: boolean }): Promise<void> {
  const token = (loadSequence += 1);
  const isCurrent = () => token === loadSequence;

  if (showSkeleton) {
    clipVersionStore.setVersions([]);
    clipVersionStore.setVersionsLoading(true);
    isSkeletonShowing = true;
  }

  try {
    let loaded = await fetchVersions(clipId);

    // A clip with no history has no baseline to fall back to, so its original state is
    // unrecoverable the moment it is first edited. Seed one from the state as loaded.
    if (isCurrent() && loaded.length === 0 && (await ensureBaselineVersion(clipId))) {
      loaded = await fetchVersions(clipId);
    }

    if (isCurrent()) {
      clipVersionStore.setVersions(loaded);
    }
  } catch (err) {
    if (isCurrent()) {
      hasFailedLoad = true;
    }
    appLogger.error('[versioning] failed to load versions', err);
  } finally {
    // Whichever load finishes last clears the skeleton, not whichever started it: a
    // background refresh can supersede the initial load, and that load then returns without
    // clearing it. Superseded loads leave it alone so the state belongs to the newest request.
    if (isCurrent() && isSkeletonShowing) {
      isSkeletonShowing = false;
      clipVersionStore.setVersionsLoading(false);
    }
  }
}

// ─── Baseline version ─────────────────────────────────────────────────────────
// The backend's first auto-version captures the clip *after* its first edit, so a clip with no
// history loses its original state when touched. Remove once the backend seeds one itself.

function buildBaselineVersionBody(clip: Clip): ClipVersionCreateWithChapters {
  // Original-language captions rebuild from the transcript; a translation has to be stored.
  const captionsRecomputable = getIsClipUsingDefaultLanguage(clip);

  return {
    captions_recomputable: captionsRecomputable,
    description: 'Original unedited recording',
    asset_metadata: (captionsRecomputable
      ? stripCaptions(clip.asset_metadata)
      : structuredClone(clip.asset_metadata)) as unknown as { [key: string]: unknown },
    // Chapters live on the broadcast, so they need snapshotting explicitly.
    chapters: getCurrentChapters(clip.id) ?? []
  };
}

/** Shared per clip so concurrent callers don't each POST a baseline. */
const baselineVersionRequests = new Map<string, Promise<boolean>>();

async function createBaselineVersion(clipId: string): Promise<boolean> {
  const clip = currentClip.getSnapshot()[clipId];
  if (!clip) {
    return false;
  }

  try {
    await assetsVersionsCreate({ asset_id: clipId, body: buildBaselineVersionBody(clip) });
    return true;
  } catch (err) {
    appLogger.error('[versioning] failed to seed baseline version', err);
    return false;
  }
}

/**
 * Seeds a baseline for a clip that has none. Safe to call from several places at once.
 * Resolves true only when a version was created, so the caller knows to refetch.
 *
 * A failed attempt is dropped from the cache so a later activation retries — a clip with no
 * baseline has nothing to restore to.
 */
function ensureBaselineVersion(clipId: string): Promise<boolean> {
  const existing = baselineVersionRequests.get(clipId);
  if (existing) {
    return existing;
  }

  const request = createBaselineVersion(clipId).then(created => {
    if (!created) {
      baselineVersionRequests.delete(clipId);
    }
    return created;
  });

  baselineVersionRequests.set(clipId, request);
  return request;
}

// ─── Version-created event ────────────────────────────────────────────────────

/**
 * Undo/redo is scoped to the current version, so a new version becomes a clean baseline and
 * the stack behind it is dropped — undo must never cross a version boundary into state the
 * version history no longer shows.
 */
function resetUndoHistory(clipId: string): void {
  const clip = currentClip.getSnapshot()[clipId];
  if (clip) {
    initHistory(clipId, clip.asset_metadata);
  }
}

function handleVersionCreated(clipId: string, versionCreatedAt: string): void {
  void loadVersionsIntoStore(clipId, { showSkeleton: false });

  // The clip holds the previewed version, so resetting undo would baseline it to the preview.
  if (clipVersionStore.isPreviewing(clipId)) {
    return;
  }

  // Only a clean baseline when it matches the editor; if edits outran it, keep them undoable.
  if (!reconcileEditLogAfterVersion(clipId, versionCreatedAt)) {
    resetUndoHistory(clipId);
  }
}

/**
 * One subscription for the module's lifetime, filtered against the active clip, so switching
 * clips neither leaks a listener nor fans the event out once per mounted component.
 */
function bindVersionCreatedListener(): void {
  if (isPusherBound) {
    return;
  }
  isPusherBound = true;

  EventBus.on(CustomEvents.ClipVersionCreated, (data: ClipVersionCreatedPayload) => {
    if (!data?.asset_id || data.asset_id !== activeClipId) {
      return;
    }
    // No created_at in the payload yet, so the local clock counts every pending edit as captured.
    handleVersionCreated(data.asset_id, data.created_at ?? new Date().toISOString());
  });
}

// ─── View mode ────────────────────────────────────────────────────────────────

function leaveViewMode(): void {
  clipVersionStore.setViewingVersion(null);
  editorRestrictionsStore.clearRestrictions(EDITOR_RESTRICTION_REASONS.VERSION_VIEW);
}

/**
 * Applies a stored version to the editor. `persistChapterChanges` writes the version's
 * chapters back to the broadcast — true only for a restore, since chapters are shared by
 * every clip cut from the recording and a preview must not touch them.
 */
async function applyVersion(
  clipId: string,
  version: ClipVersion,
  { persistChapterChanges }: { persistChapterChanges: boolean }
): Promise<void> {
  const detail = (await assetsVersionsRetrieve({
    asset_id: clipId,
    version_index: version.version_index
  })) as ClipVersionDetailWithChapters;

  const meta = buildRestoredMetadata(clipId, detail);
  if (meta) {
    updateClipMetadataWithId(clipId, meta, true);
  }

  // Versions predating the chapters field carry no snapshot; leaving the current chapters
  // alone is better than clearing them on the strength of a missing key.
  if (!detail.chapters) {
    return;
  }
  if (persistChapterChanges) {
    persistChapters(clipId, detail.chapters);
  } else {
    applyChaptersLocally(detail.chapters);
  }
}

/**
 * Saves the clip as it currently stands in the store. Restore only writes to the local store,
 * and the recomputed captions exist nowhere on the server (versions are stored with captions
 * stripped) — without this the clip would revert on reload. Routed through addPendingUpdate so
 * a restore blocks publish/navigation like any edit.
 */
async function persistClip(clipId: string): Promise<void> {
  const clip = currentClip.getSnapshot()[clipId];
  if (!clip) {
    return;
  }
  const version = getClipVersion();
  const updatePromise = updateClip({ ...clip, version }).then(updateCurrentClipUpdatedAt);
  addPendingUpdate({ clipId, updatePromise, version });
  await updatePromise;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Points versioning at the clip now open in the editor. Idempotent: a repeat call for the
 * clip already loaded does nothing, so every component rendering versioning UI can call this
 * on mount. A clip whose load failed is the exception — the next call retries it, so opening
 * the panel after a failed load is not stuck with an empty list.
 */
export function activateVersioning(clipId: string): void {
  if (!clipId || !isVersioningEnabled()) {
    return;
  }

  const isSameClip = clipId === activeClipId;
  if (isSameClip && !hasFailedLoad) {
    return;
  }

  // View mode is in-memory only, so it must not survive a switch to another clip — otherwise
  // the new clip opens stuck in read-only with no banner to exit from.
  if (!isSameClip && activeClipId) {
    clipVersionStore.takePreViewSnapshot(activeClipId);
    leaveViewMode();
  }

  activeClipId = clipId;
  hasFailedLoad = false;
  bindVersionCreatedListener();
  void loadVersionsIntoStore(clipId, { showSkeleton: true });
}

/** Drops all version state. For leaving the editor entirely. */
export function resetVersioning(): void {
  activeClipId = null;
  loadSequence += 1;
  isSkeletonShowing = false;
  hasFailedLoad = false;
  baselineVersionRequests.clear();
  clipVersionStore.reset();
  editorRestrictionsStore.clearRestrictions(EDITOR_RESTRICTION_REASONS.VERSION_VIEW);
}

/** Previews a version without writing anything the user has to undo. */
export async function viewVersion(version: ClipVersion): Promise<void> {
  const clipId = activeClipId;
  const clip = clipId ? currentClip.getSnapshot()[clipId] : undefined;
  if (!clipId || !clip) {
    return;
  }

  try {
    // Capture the live state before overwriting it — Exit restores this, not the latest saved
    // version, so edits not yet captured in a version survive a preview.
    clipVersionStore.setPreViewSnapshot(clipId, structuredClone(clip.asset_metadata), getCurrentChapters(clipId));
    clearEditLog(clipId);
    await applyVersion(clipId, version, { persistChapterChanges: false });
  } catch (err) {
    appLogger.error('[versioning] failed to load version for preview', err);
    // Never enter view mode on a failed preview — discard the snapshot so a later Exit cannot
    // reapply stale state.
    clipVersionStore.takePreViewSnapshot(clipId);
    return;
  }

  clipVersionStore.setViewingVersion(version, clipId);
  editorRestrictionsStore.setRestrictions(EDITOR_RESTRICTION_REASONS.VERSION_VIEW, [
    ...VERSION_VIEW_BLOCKED_CAPABILITIES
  ]);
}

/** Restores a version, recording the restore as a version of its own. */
export async function restoreVersion(version: ClipVersion): Promise<void> {
  const clipId = activeClipId;
  if (!clipId) {
    return;
  }

  // Restore is not re-entrant: it posts a version and rewrites the clip, so a second click
  // mid-flight would race the first.
  if (clipVersionStore.getSnapshot().restoringVersion !== null) {
    return;
  }
  clipVersionStore.setRestoringVersion(version);

  // The restore supersedes any preview, and any pending edits are replaced by the restored
  // state — drop both so nothing reapplies them afterwards.
  clearEditLog(clipId);
  clipVersionStore.takePreViewSnapshot(clipId);

  try {
    const detail = (await assetsVersionsRestoreCreate({
      asset_id: clipId,
      version_index: version.version_index
    })) as ClipVersionDetailWithChapters;

    const meta = buildRestoredMetadata(clipId, detail);
    if (meta) {
      // Reset again on top of the restored metadata: the earlier clearEditLog wrote these, but
      // the version's own snapshot of them would otherwise overwrite it here.
      updateClipMetadataWithId(
        clipId,
        { ...meta, pending_action_labels: [], first_updated_at_after_last_version: null },
        true
      );
      await persistClip(clipId);
    }
    // Broadcast-scoped: this replaces the chapter list for every clip cut from the same
    // recording. Skipped when the version predates the chapters field.
    if (detail.chapters) {
      persistChapters(clipId, detail.chapters);
    }
  } catch (err) {
    appLogger.error('[versioning] restore failed', err);
  } finally {
    void loadVersionsIntoStore(clipId, { showSkeleton: false });
    // The restore records its own version, and it replaced the metadata wholesale — without
    // this, undo would step back to the pre-restore clip.
    resetUndoHistory(clipId);
    // Always leave view mode, even on failure — otherwise the editor is stuck read-only with
    // no way back.
    leaveViewMode();
    // Cleared last so the restoring state cannot outlive a failure and leave the panel
    // spinning with no way out.
    clipVersionStore.setRestoringVersion(null);
  }
}

/**
 * Leaves the preview and puts the pre-view state back. Synchronous and local: the snapshot is
 * held in memory, so exiting cannot fail on a network call and can never leave the editor stuck.
 */
export function exitViewMode(): void {
  const snapshot = activeClipId ? clipVersionStore.takePreViewSnapshot(activeClipId) : null;
  if (snapshot) {
    updateClipMetadataWithId(snapshot.clipId, snapshot.metadata, true);
    // Local only, mirroring the preview: the preview never wrote chapters to the broadcast, so
    // putting the originals back needs no request either.
    applyChaptersLocally(snapshot.chapters);
  }
  leaveViewMode();
}
